import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useSession, activeMembership } from './lib/store';
import { Spinner } from './components/Shell';
import { brandStyle } from './lib/branding';
import { SiteHeader } from './components/SiteHeader';
import { Storefront } from './pages/Storefront';
import { SignIn } from './pages/SignIn';
import { SignUp } from './pages/SignUp';
import { Onboarding } from './pages/Onboarding';
import { SignAgreement } from './pages/SignAgreement';
import { AcceptInvite } from './pages/AcceptInvite';
import { JoinInvite } from './pages/JoinInvite';
import { NotFound } from './pages/NotFound';
import { PortalHome } from './pages/portal/PortalHome';
import { PetRecord } from './pages/portal/PetRecord';
import { MyDetails } from './pages/portal/MyDetails';
import { NewBooking } from './pages/portal/NewBooking';
import { BookingDetail } from './pages/portal/BookingDetail';
import { ClientVideos } from './pages/portal/ClientVideos';
import { AdminHome } from './pages/admin/AdminHome';
import { AdminSchedule } from './pages/admin/AdminSchedule';
import { AdminBooking } from './pages/admin/AdminBooking';
import { AdminClients } from './pages/admin/AdminClients';
import { AdminServices } from './pages/admin/AdminServices';
import { AdminSettings } from './pages/admin/AdminSettings';
import { AdminVideos } from './pages/admin/AdminVideos';
import { AdminInvoices } from './pages/admin/AdminInvoices';
import { AdminReports } from './pages/admin/AdminReports';

export function App() {
  const { status, tenant, load } = useSession();

  useEffect(() => {
    void load();
  }, [load]);

  if (status === 'loading') {
    return (
      <div className="mx-auto max-w-md px-6 py-32">
        <Spinner label="Opening the file" />
      </div>
    );
  }

  return (
    // The sitter's colours are applied here rather than inside the storefront,
    // so the header and every signed-in screen carry them too. With no business
    // in context this is empty and the platform's own palette shows through.
    <div style={brandStyle(tenant?.branding)}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <SiteHeader />
      <main id="main">
        <Routes>
          {/* Public signing link. No account needed; the token is the credential. */}
          <Route path="/sign/:token" element={<SignAgreement />} />

          {/* The other end of a staff invitation email. Not role-guarded: the
              person arriving has no membership yet, which is the point. */}
          <Route path="/staff/accept/:token" element={<AcceptInvite />} />

          {/* And of a client invitation. Public for the same reason, but it
              leads with a page rather than redeeming on arrival: it goes to
              someone who has not agreed to anything yet. */}
          <Route path="/join/:token" element={<JoinInvite />} />

          <Route path="/" element={<Storefront />} />
          <Route path="/b/:slug" element={<Storefront />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/start-a-business" element={<Onboarding />} />

          <Route path="/portal" element={<RequireRole role="client"><PortalHome /></RequireRole>} />
          <Route path="/portal/details" element={<RequireRole role="client"><MyDetails /></RequireRole>} />
          <Route path="/portal/pets/:petId" element={<RequireRole role="client"><PetRecord /></RequireRole>} />
          <Route path="/portal/pets/new" element={<RequireRole role="client"><PetRecord /></RequireRole>} />
          <Route path="/portal/book" element={<RequireRole role="client"><NewBooking /></RequireRole>} />
          <Route path="/portal/bookings/:bookingId" element={<RequireRole role="client"><BookingDetail /></RequireRole>} />
          <Route path="/portal/videos" element={<RequireRole role="client"><ClientVideos /></RequireRole>} />

          <Route path="/admin" element={<RequireRole role="staff"><AdminHome /></RequireRole>} />
          <Route path="/admin/schedule" element={<RequireRole role="staff"><AdminSchedule /></RequireRole>} />
          <Route path="/admin/bookings/:bookingId" element={<RequireRole role="staff"><AdminBooking /></RequireRole>} />
          <Route path="/admin/clients" element={<RequireRole role="staff"><AdminClients /></RequireRole>} />
          <Route path="/admin/videos" element={<RequireRole role="staff"><AdminVideos /></RequireRole>} />
          <Route path="/admin/invoices" element={<RequireRole role="staff"><AdminInvoices /></RequireRole>} />
          <Route path="/admin/reports" element={<RequireRole role="staff"><AdminReports /></RequireRole>} />
          <Route path="/admin/services" element={<RequireRole role="owner"><AdminServices /></RequireRole>} />
          <Route path="/admin/settings" element={<RequireRole role="owner"><AdminSettings /></RequireRole>} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </div>
  );
}

const RANK: Record<string, number> = { client: 1, staff: 2, owner: 3 };

/**
 * Route guard. An unauthenticated visitor is sent to sign in with a return
 * path; a signed-in person without the right role is sent somewhere they can
 * actually use rather than shown a dead end.
 */
function RequireRole({ role, children }: { role: 'client' | 'staff' | 'owner'; children: JSX.Element }) {
  const session = useSession();
  const location = useLocation();
  const membership = activeMembership(session);

  if (!session.user) {
    return <Navigate to="/sign-in" state={{ from: location.pathname + location.search }} replace />;
  }
  if (!session.tenant) {
    return <Navigate to="/" replace />;
  }
  if (!membership) {
    // Signed in, but not yet a client of this business.
    return <Navigate to="/" replace />;
  }
  if (RANK[membership.role] < RANK[role]) {
    return <Navigate to={membership.role === 'client' ? '/portal' : '/admin'} replace />;
  }
  // The portal is not a lesser version of the admin screens, it is the other
  // side of the business: one client's own pets and bookings. A sitter has no
  // client record here, so the rank comparison above is the wrong test for it
  // and would hand them an empty file with their own name on it.
  if (role === 'client' && membership.role !== 'client') {
    return <Navigate to="/admin" replace />;
  }

  return children;
}
